import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PhoneStep } from "./steps/PhoneStep";
import { OTPStep } from "./steps/OTPStep";
import { NameStep } from "./steps/NameStep";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

type SignupStep = "phone" | "otp" | "name";

interface SignupData {
  phone?: string;
  name?: string;
}

/**
 * WhatsApp-based signup flow component
 * Handles the complete signup process using WhatsApp verification
 */
export const WhatsAppSignupFlow = () => {
  const [currentStep, setCurrentStep] = useState<SignupStep>("phone");
  const [signupData, setSignupData] = useState<SignupData>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handlePhoneNext = (data: { phone: string }) => {
    setSignupData((prev) => ({ ...prev, phone: data.phone }));
    setCurrentStep("otp");
  };

  const handleOTPNext = () => {
    setCurrentStep("name");
  };

  const handleNameNext = async (data: { name: string }) => {
    setLoading(true);
    try {
      setSignupData((prev) => ({ ...prev, name: data.name }));

      // Create account with temporary email (will be updated later if user provides one)
      const tempEmail = `${signupData.phone?.replace(/\D/g, "")}@whatsapp.temp`;
      const tempPassword = Math.random().toString(36).slice(-12) + 'Aa1!'; // Generate random secure password

      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email: tempEmail,
        password: tempPassword,
        options: {
          data: {
            name: data.name,
            phone: signupData.phone,
            verified_via: "whatsapp",
          },
        },
      });

      if (signupError) throw signupError;

      logger.info("WhatsApp signup successful", { userId: authData.user?.id });

      toast({
        title: "Conta criada!",
        description: "Bem-vindo ao You Better Run! 🎉",
      });

      navigate("/");
    } catch (error: any) {
      logger.error("WhatsApp signup failed", error);
      toast({
        variant: "destructive",
        title: "Erro ao criar conta",
        description: error.message || "Tente novamente mais tarde.",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case "phone":
        return <PhoneStep onNext={handlePhoneNext} />;
      case "otp":
        return (
          <OTPStep
            onNext={handleOTPNext}
            identifier={signupData.phone!}
            verificationType="whatsapp"
            allowSkip={false}
          />
        );
      case "name":
        return <NameStep onNext={handleNameNext} initialData={{}} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
